import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ShoppingCart, Package, CheckCircle, Loader2, AlertCircle, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useRegion } from "@/components/RegionContext";
import { useLanguage } from "@/components/LanguageContext";
import { trackInitiateCheckout, trackPurchase } from "@/lib/pixel";
import { createStripeCheckout } from "@/functions/createStripeCheckout";
import { createPageUrl } from "@/utils";
import { base44 } from "@/api/base44Client";

// PRODUCTION PRICE (display only; the charged currency/amount is selected
// server-side by the detected region via the createStripeCheckout function)
const PRODUCT_PRICE = 399;


export default function CheckoutPage() {
  const { region, currency, formatLocalPrice } = useRegion();
  const { language } = useLanguage();
  // Region-aware default country (ME only — UAE or Saudi Arabia). Kept in sync
  // with the detected region until the customer starts typing a custom value.
  const ME_COUNTRY_DEFAULTS = ["United Arab Emirates", "Saudi Arabia"];
  const [quantity, setQuantity] = useState(1);
  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    phone: "",
    shipping_address: "",
    city: "",
    state: "",
    zip_code: "",
    country: region === "sa" ? "Saudi Arabia" : "United Arab Emirates",
    order_notes: ""
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [completedOrder, setCompletedOrder] = useState(null);

  // If region changes (e.g. IP detection finished after first render) and the
  // customer hasn't customized the country yet, follow the detected region.
  useEffect(() => {
    setFormData((prev) => {
      if (ME_COUNTRY_DEFAULTS.includes(prev.country)) {
        return { ...prev, country: region === "sa" ? "Saudi Arabia" : "United Arab Emirates" };
      }
      return prev;
    });
  }, [region]);
  // Whether the active region uses VAT (UAE & Saudi Arabia)
  const isMiddleEast = region === "uae" || region === "sa";

  // Handle return from Stripe Checkout (success / cancel) — preserves the
  // existing confirmation flow (Order record + confirmation emails + success screen).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    const orderId = params.get("order");
    const sessionId = params.get("session_id");
    if (status === "success" && orderId && sessionId) {
      setIsProcessing(true);
      createStripeCheckout({ mode: "verify", session_id: sessionId, order_id: orderId })
        .then((res) => {
          const data = (res && res.data) || {};
          if (data.paid) {
            const orderData = data.order || {};
            // Meta conversion signal: payment confirmed by Stripe. order.total_amount
            // is stored in USD (server-side), but Stripe actually charges the
            // customer in their local currency (AED/SAR) at the pegged rate — so
            // convert to the real charged amount here. Previously this always
            // reported "399 USD" even when the Gulf customer paid AED/SAR,
            // corrupting Meta's value/ROAS data for exactly the traffic we're
            // trying to optimize for.
            const localAmount = (Number(orderData.total_amount) || 0) * currency.rate;
            trackPurchase(localAmount, orderId, orderData.quantity || 1, currency.code);
            setCompletedOrder(orderData);
            setCreatedOrderId(orderId);
            setOrderSuccess(true);
          } else {
            setPaymentError("Payment not completed yet. If you already paid, contact support.");
          }
        })
        .catch(() => setPaymentError("Could not verify your payment. Please contact support."))
        .finally(() => setIsProcessing(false));
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (status === "cancel" && orderId) {
      setPaymentError("Payment was cancelled. No charge has been made — you can try again anytime.");
      // Mark the abandoned order as cancelled so it doesn't linger as pending.
      createStripeCheckout({ mode: "cancel", order_id: orderId }).catch(() => {});
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalAmount = PRODUCT_PRICE * quantity;
  const formDataRef = useRef(formData);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);




  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setPaymentError(null);
  };

  const handleStripeCheckout = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.email || !formData.phone || 
        !formData.shipping_address || !formData.city || !formData.state || !formData.zip_code || !formData.country) {
      setPaymentError("Please fill in all required fields, including your full contact information and shipping address, before payment.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setPaymentError("Please enter a valid email address.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Stripe Checkout cannot run inside the Base44 editor iframe preview —
    // it only works from the published app. Detect the iframe and block.
    try {
      if (window.self !== window.top) {
        setPaymentError("Checkout works only from the published app. Please publish your app and open it in a new tab to complete payment.");
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    } catch (_) {
      // cross-origin access to window.top throws inside an iframe — same conclusion
      setPaymentError("Checkout works only from the published app. Please publish your app and open it in a new tab to complete payment.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    let createdOrderId = null;
    try {
      // The Order record is now created server-side (in createStripeCheckout,
      // mode: "create") using asServiceRole, so an anonymous client can't call
      // entities.Order.create directly and forge order_status/payment_status.
      // We only send the customer/shipping fields the backend needs; the
      // backend hardcodes order_status/payment_status/total_amount itself.
      const stripeResp = await createStripeCheckout({
        mode: "create",
        quantity,
        email: formData.email,
        region,
        customer: {
          customer_name: formData.customer_name,
          phone: formData.phone,
          shipping_address: formData.shipping_address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          country: formData.country,
          order_notes: formData.order_notes,
        },
        // Force the Stripe Checkout page language to the store's language
        // (English or Arabic) — never the browser locale.
        locale: language === "ar" ? "ar" : "en",
      });
      createdOrderId = stripeResp.data && stripeResp.data.order_id;
      // Meta conversion signal: the shopper is being sent to Stripe to pay.
      // totalAmount is a USD baseline (PRODUCT_PRICE * quantity) -- convert to
      // the actual local currency Stripe will charge, same as trackPurchase.
      trackInitiateCheckout(quantity, { value: totalAmount * currency.rate, currency: currency.code });
      const stripeUrl = stripeResp.data && stripeResp.data.url;
      if (!stripeUrl) throw new Error("No checkout URL returned");
      // Redirect to Stripe Checkout in same window for better reliability
      window.location.href = stripeUrl;
      
    } catch (err) {
      console.error("Stripe checkout error:", err);
      // If we created an Order record but never reached Stripe Checkout, mark it
      // cancelled so it doesn't linger as a pending/awaiting_payment orphan.
      if (createdOrderId) {
        createStripeCheckout({ mode: "cancel", order_id: createdOrderId }).catch(() => {});
      }
      setPaymentError("Failed to initiate checkout. Please try again.");
      setIsProcessing(false);
    }
  };



  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-2xl w-full">
          <Card className="border-0 shadow-2xl">
            <CardContent className="pt-16 pb-16 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-24 h-24 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-xl"
              >
                <CheckCircle className="w-14 h-14 text-white" />
              </motion.div>
              <h2 className="text-4xl font-black text-gray-900 mb-4">Order Confirmed!</h2>
              <p className="text-xl text-gray-600 mb-8">Thank you! Your payment has been processed successfully.</p>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200">
                <p className="text-green-800 font-semibold text-lg mb-2">📧 Confirmation sent to</p>
                <p className="text-green-900 font-bold text-xl mb-4">{completedOrder?.email || formData.email}</p>
                <p className="text-green-700 text-sm mb-3">Order Amount: {formatLocalPrice(completedOrder?.total_amount != null ? completedOrder.total_amount : totalAmount)} (inc. {isMiddleEast ? "VAT" : "GST"})</p>
                {createdOrderId && (
                  <p className="text-green-600 text-xs font-mono">Order ID: {createdOrderId.slice(0, 16)}...</p>
                )}
              </div>
              <div className="mt-8 text-sm text-gray-600">
                <p className="mb-2">✅ Payment confirmed</p>
                <p className="mb-2">✅ Order confirmation emailed</p>
                <p>✅ Tracking number will be sent within 24-48 hours</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <h1 className="text-5xl font-black text-gray-900 mb-4">Secure Checkout</h1>
          <p className="text-2xl text-gray-600">Complete your order with secure payment</p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card className="shadow-2xl border-0">
              <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white p-8">
                <CardTitle className="flex items-center gap-3 text-3xl font-black">
                  <Package className="w-8 h-8" />
                  Order Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                {paymentError && (
                  <Alert className="mb-6 bg-red-50 border-red-300 border-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <AlertDescription className="text-red-900 font-semibold">{paymentError}</AlertDescription>
                  </Alert>
                )}

                <form className="space-y-6">
                  {/* Contact Information */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="customer_name">Full Name *</Label>
                        <Input
                          id="customer_name"
                          value={formData.customer_name}
                          onChange={(e) => handleInputChange("customer_name", e.target.value)}
                          placeholder="John Doe"
                          required
                          className="h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          placeholder="john@example.com"
                          required
                          className="h-12"
                        />
                      </div>
                    </div>
                    <div className="space-y-2 mt-6">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        placeholder={region === "uae" ? "+971 50 000 0000" : region === "sa" ? "+966 50 000 0000" : "+61 400 000 000"}
                        required
                        className="h-12"
                      />
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Shipping Address</h3>
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="shipping_address">Street Address *</Label>
                        <Input
                          id="shipping_address"
                          value={formData.shipping_address}
                          onChange={(e) => handleInputChange("shipping_address", e.target.value)}
                          placeholder="123 Main Street"
                          required
                          className="h-12"
                        />
                      </div>
                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="city">City *</Label>
                          <Input
                            id="city"
                            value={formData.city}
                            onChange={(e) => handleInputChange("city", e.target.value)}
                            placeholder={region === "sa" ? "Riyadh" : "Dubai"}
                            required
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="state">{region === "uae" ? "Emirate *" : region === "sa" ? "Province *" : "State *"}</Label>
                          <Input
                            id="state"
                            value={formData.state}
                            onChange={(e) => handleInputChange("state", e.target.value)}
                            placeholder={region === "uae" ? "Dubai" : region === "sa" ? "Riyadh Province" : "NSW"}
                            required
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="zip_code">Postal Code *</Label>
                          <Input
                            id="zip_code"
                            value={formData.zip_code}
                            onChange={(e) => handleInputChange("zip_code", e.target.value)}
                            placeholder={isMiddleEast ? "00000" : "2000"}
                            required
                            className="h-12"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="country">Country *</Label>
                        <Input
                          id="country"
                          value={formData.country}
                          onChange={(e) => handleInputChange("country", e.target.value)}
                          placeholder="United Arab Emirates"
                          required
                          className="h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="order_notes">Order Notes (Optional)</Label>
                        <Textarea
                          id="order_notes"
                          value={formData.order_notes}
                          onChange={(e) => handleInputChange("order_notes", e.target.value)}
                          placeholder="Any special instructions..."
                          className="h-24"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payment Methods */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Payment Method</h3>
                    
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6 space-y-4">

                      {/* Stripe Payment Button */}
                      <div>
                        <button
                          onClick={handleStripeCheckout}
                          disabled={isProcessing}
                          type="button"
                          className="w-full bg-[#635BFF] hover:bg-[#5469D4] disabled:bg-gray-400 disabled:cursor-not-allowed text-white h-14 text-base font-semibold rounded-md shadow-sm transition-all flex items-center justify-center gap-3"
                        >
                          {isProcessing ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              <span>Opening secure payment...</span>
                            </>
                          ) : (
                            <>
                              <span>Pay with</span>
                              <svg viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg" width="60" height="25" className="inline-block">
                                <path fill="white" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.70c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.10 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.85 6.29.97 6.29 5.88z" fillRule="evenodd"/>
                              </svg>
                            </>
                          )}
                        </button>
                        <div className="mt-2 flex items-center justify-center gap-2 text-xs text-gray-500">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                          <span>Secure payment • Opens in new window</span>
                        </div>
                      </div>

                      {/* COD Payment Button - opens WhatsApp with the filled-in order details */}
                      <div>
                        <button
                          type="button"
                          onClick={() => {
                            // Validate the form first so the customer doesn't lose
                            // their captured details — same check as the Stripe path.
                            const required = ["customer_name","email","phone","shipping_address","city","state","zip_code","country"];
                            const missing = required.some((f) => !String(formData[f] || "").trim());
                            if (missing) {
                              setPaymentError("Please fill in all required fields, including your full contact information and shipping address, before sending your COD order.");
                              window.scrollTo({ top: 0, behavior: "smooth" });
                              return;
                            }
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            if (!emailRegex.test(formData.email)) {
                              setPaymentError("Please enter a valid email address.");
                              window.scrollTo({ top: 0, behavior: "smooth" });
                              return;
                            }
                            setPaymentError(null);
                            base44.analytics.track({
                              eventName: "cod_payment_clicked",
                              properties: { quantity, total_usd: totalAmount, source: "checkout_page" }
                            });
                            const params = new URLSearchParams({
                              quantity: String(quantity),
                              customer_name: formData.customer_name,
                              email: formData.email,
                              phone: formData.phone,
                              shipping_address: formData.shipping_address,
                              city: formData.city,
                              state: formData.state,
                              zip_code: formData.zip_code,
                              country: formData.country,
                              order_notes: formData.order_notes || "",
                            });
                            window.location.href = createPageUrl("CODCheckout") + "?" + params.toString();
                          }}
                          className="w-full bg-[#25D366] hover:bg-[#1FB857] text-white h-14 text-base font-semibold rounded-md shadow-sm transition-all flex items-center justify-center gap-3"
                        >
                          <MessageCircle className="w-5 h-5" />
                          COD Payment
                        </button>
                        <div className="mt-2 flex items-center justify-center gap-2 text-xs text-gray-500">
                          <MessageCircle className="w-3 h-3" />
                          <span>Pay on delivery via WhatsApp · Cash on Delivery</span>
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Important Notice */}
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm font-semibold text-red-900">
                        <strong>Important:</strong> This product requires an air compressor with minimum 8 Bar pressure and 8-10.5 gallon capacity. Air compressor is not included.
                      </p>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="shadow-2xl sticky top-24">
              <CardHeader className="bg-gray-900 text-white p-6">
                <CardTitle className="flex items-center gap-3 text-2xl font-black">
                  <ShoppingCart className="w-6 h-6" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex gap-4">
                  <img
                    src="https://res.cloudinary.com/dilx8zm2p/image/upload/f_auto,q_auto,w_200/v1759783025/1_iatrwm.jpg"
                    alt="LCTX Tire Changer"
                    className="w-20 h-20 object-cover rounded"
                    width="200"
                    height="200"
                    loading="lazy"
                  />
                  <div>
                    <h3 className="font-bold">LCTX Tyre Changer</h3>
                    <p className="text-sm text-gray-600">Air-Powered, 12-21"</p>
                  </div>
                </div>

                <div className="flex items-center justify-between py-4 border-y">
                  <span>Quantity</span>
                  <div className="flex items-center gap-3">
                    <motion.button
                      type="button"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-8 h-8 border rounded hover:bg-gray-100 dark:hover:bg-gray-800 dark:border-gray-600 select-none"
                      whileTap={{ scale: 0.9 }}
                      transition={{ duration: 0.1 }}
                    >
                      -
                    </motion.button>
                    <motion.span 
                      key={quantity}
                      initial={{ scale: 1.2, color: '#dc2626' }}
                      animate={{ scale: 1, color: '#000000' }}
                      transition={{ duration: 0.15 }}
                      className="font-bold w-8 text-center dark:text-white"
                    >
                      {quantity}
                    </motion.span>
                    <motion.button
                      type="button"
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-8 h-8 border rounded hover:bg-gray-100 dark:hover:bg-gray-800 dark:border-gray-600 select-none"
                      whileTap={{ scale: 0.9 }}
                      transition={{ duration: 0.1 }}
                    >
                      +
                    </motion.button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>Price ({currency.en})</span>
                    <span>{formatLocalPrice(PRODUCT_PRICE)}</span>
                  </div>
                  {!isMiddleEast && (
                    <div className="flex justify-between text-gray-600 text-sm">
                      <span>GST (Included)</span>
                      <span>{formatLocalPrice(totalAmount / 11)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span className="text-green-600 font-bold">FREE</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold">{isMiddleEast ? "Total (incl. VAT)" : "Total (inc. GST)"}</span>
                      <span className="text-2xl font-black text-red-600">{formatLocalPrice(totalAmount)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}